<template>
    <ToggleGroupRoot
        v-bind="field?.controlAttrs.value"
        :aria-labelledby="field?.labelId"
        :model-value="modelValue ?? undefined"
        :required="required"
        :type="type"
        class="toggle-group"
        :class="[appearance, { 'self-start': field, 'is-invalid': invalid }]"
        @update:model-value="onSelected">
        <ToggleGroupItem v-for="option of options" :key="option.key?.toString()" :value="option.key" class="option">
            <slot name="option" :option="option">{{ option.label }}</slot>
        </ToggleGroupItem>
    </ToggleGroupRoot>
</template>

<script setup lang="ts" generic="K extends AcceptableValue, M extends K | K[] | null">
import { useFormField } from "~vue/components/form/formFieldContext";
import { type AcceptableValue, ToggleGroupItem, ToggleGroupRoot } from "reka-ui";
import { computed } from "vue";

type BaseToggleGroupProps = {
    options: { key: K; label: string }[];
    // A single key, or an array of keys for type="multiple"; null only when a single group is not required.
    modelValue: M;
    type?: "single" | "multiple";
    // When true, the last selected option cannot be deselected; when false, a single group clears to null.
    required?: boolean;
    // "segmented" is a pick-one track of equal options; "chips" wrap in a row; "tiles" are equal
    // columns for a short fixed scale.
    appearance?: "segmented" | "chips" | "tiles";
};

const { appearance = "segmented", required = true, type = "single" } = defineProps<BaseToggleGroupProps>();

// Inside a FormField the group is named by the field's label (a `for` cannot reach a group) and
// takes its id, describedby and invalid wiring; while invalid, the options take BaseInput's
// invalid border. An `id` or `aria-labelledby` passed as an attribute still wins, since
// fallthrough attrs override these bindings. FormField stacks its parts in a flex column, so the
// group opts out of stretching to keep its options at their natural width.
const field = useFormField();
const invalid = computed(() => field?.invalid.value ?? false);

const emit = defineEmits<{
    "update:modelValue": [payload: M];
}>();

const onSelected = (value: AcceptableValue | AcceptableValue[] | undefined) => {
    // reka-ui reports a deselected single group as undefined and a deselected multiple group as [].
    const isEmpty = Array.isArray(value) ? value.length === 0 : value === undefined;
    if (isEmpty && required) {
        return;
    }

    // reka-ui types the payload loosely; the group only ever selects its own option keys.
    emit("update:modelValue", (value ?? null) as M);
};
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .toggle-group {
        @apply inline-flex items-start;
    }

    .toggle-group.chips {
        @apply flex-wrap gap-2;
    }

    .toggle-group.tiles {
        @apply grid w-full auto-cols-fr grid-flow-col gap-2;
    }

    /* A borderless track whose radius is the button radius plus its inset, so the curves stay parallel.
       The proposal's 2px inset is off the spacing scale, so it uses the smallest step, 4px. */
    .toggle-group.segmented {
        @apply inline-grid auto-cols-fr grid-flow-col gap-1 p-1 rounded-[calc(var(--radius-button)+4px)] bg-surface-sunken;
    }

    .option {
        @apply base-btn-base base-btn-sm base-btn-with-icon border;
    }

    .chips .option,
    .tiles .option {
        @apply data-[state=on]:bg-toggle-selected data-[state=on]:border-toggle-selected-border data-[state=on]:text-ink data-[state=off]:hover:bg-button-ghost-hover data-[state=off]:disabled:bg-button-neutral-disabled data-[state=off]:disabled:border-button-neutral-disabled-border disabled:text-button-disabled-text bg-button-neutral border-button-neutral-border text-button-neutral-text;
    }

    .tiles .option {
        @apply py-4 whitespace-normal;
    }

    .segmented .option {
        @apply data-[state=on]:bg-surface-raised data-[state=on]:border-border-strong data-[state=on]:text-ink data-[state=off]:hover:bg-button-ghost-hover disabled:text-button-disabled-text border-transparent text-ink-muted;
    }

    .is-invalid .option {
        @apply border-status-error;
    }

    /* Declared last so the shared focus treatment wins over every appearance's border. */
    .toggle-group .option {
        @apply focus-field;
    }
}
</style>
